import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { iif, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, share, switchMap } from 'rxjs/operators';
import { AuthResult } from '../generated/auth/models/Sasfin/Wealth/Online/Auth/Models/auth-result';
import { AuthService as AuthExternalService } from '../generated/auth/services/auth.service';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '../shared/utils/app.const';
import { ERROR_CODE, ERROR_PAGE_TYPES } from '../shared/utils/errors.helper';
import { expiredAccess } from '../store/auth/auth.actions';
import { AuthEffect } from '../store/auth/auth.effects';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: Observable<any> | null = null;

  constructor(
    private authExternalService: AuthExternalService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private router: Router,
    private store: Store<AuthEffect>,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.getRequest(request).pipe(
      concatMap((request) => next.handle(request)),
      catchError((error: HttpErrorResponse) => {
        let clientError = '';
        let displayError = '';
        let serverError = '';

        if (error.error instanceof ErrorEvent) {
          clientError = `Error: ${error.error.message}`;
        } else {
          if (error?.error?.errorCode === 'Validation_Error') {
            const cleanedMessage = this.cleanValidationErrorMessage(error.error.errorMessage);
            this.notificationService.error(cleanedMessage, 'Validation Error');
          }

          // server-side error
          displayError = error.error;
          if (error?.error?.exception?.Message) {
            serverError = `${error?.error?.exception?.Message}`;
          } else {
            serverError = `${error.error?.errorMessage || error.message}`;
          }

          // Error Too Many Requests per minute
          if (error.status === 429) {
            this.router.navigate(['error', ERROR_PAGE_TYPES.USER_BLOCKED]);
          }

          if (error.status === 401) {
            return this.handle401Error(request, next, error);
          } else {
            if (error?.error.hasOwnProperty('errorCode')) {
              if (error.error.errorCode?.toLowerCase() === ERROR_CODE.USER_TEMP_BLOCKED.toLowerCase()) {
                this.router.navigate(['error', ERROR_PAGE_TYPES.USER_TEMP_BLOCKED]);
              }
              if (error.error.errorCode?.toLowerCase() === ERROR_CODE.USER_DECEASED.toLowerCase()) {
                this.router.navigate(['error', ERROR_PAGE_TYPES.USER_DECEASED]);
              }
              if (error.error.errorCode?.toLowerCase() === ERROR_CODE.USER_DYNAMICS_NOT_ACTIVE.toLowerCase()) {
                this.router.navigate(['error', ERROR_PAGE_TYPES.USER_DYNAMICS_NOT_ACTIVE]);
              }

              // !this.skipCode(error?.error.errorCode) && this.notificationService.error(serverError);
            } else {
              // !this.skipCode(this.tryParseCode(error?.error)) && this.notificationService.error(serverError);
            }
          }
        }
        return throwError(displayError);
      }),
    );
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    if (
      req.url.includes('/Auth/Account/Refresh') ||
      req.url.includes('/Auth/Account/Login') ||
      req.url.includes('/Auth/Account/GetResetPasswordToken')
    ) {
      return throwError(error);
    }

    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject!.pipe(
        switchMap((response) => {
          if (response && response.isSuccessful) {
            const clonedReq = req.clone({
              setHeaders: {
                Authorization: 'Bearer ' + response.result.accessToken,
              },
            });
            return next.handle(clonedReq);
          } else {
            return throwError(response || error);
          }
        }),
      );
    }

    this.refreshTokenInProgress = true;

    this.refreshTokenSubject = of([sessionStorage.getItem(ACCESS_TOKEN), sessionStorage.getItem(REFRESH_TOKEN)]).pipe(
      switchMap(([token, refreshToken]) =>
        iif(
          () => !!token && !!refreshToken,
          this.authExternalService.authAccountRefreshPost({ body: { token, refreshToken } }),
          of(null),
        ),
      ),
      switchMap((response) => {
        this.refreshTokenInProgress = false;

        if (response && response.isSuccessful) {
          const authResult: AuthResult = {
            userInfo: null,
            authTokens: response.result,
          };

          this.authService.setToken(authResult);
          return of(response);
        } else {
          this.refreshTokenSubject = null;
          this.store.dispatch(expiredAccess());
          return throwError(response);
        }
      }),
      catchError((refreshError) => {
        this.refreshTokenInProgress = false;
        this.refreshTokenSubject = null;
        this.store.dispatch(expiredAccess());
        return throwError(refreshError);
      }),
      share(),
    );

    return this.refreshTokenSubject.pipe(
      switchMap((response) => {
        if (response && response.isSuccessful) {
          const clonedReq = req.clone({
            setHeaders: {
              Authorization: 'Bearer ' + response.result.accessToken,
            },
          });
          return next.handle(clonedReq);
        } else {
          return throwError(response);
        }
      }),
    );
  }

  // tryParseCode(json: any | string): string {
  //   try {
  //     return (json as any).code ?? JSON.parse(json).code;
  //   } catch (any) {}
  //   return '';
  // }

  // private skipCode(code: string): boolean {
  //   const codes: string[] = [
  //     ERROR_CODE.ACCOUNT_OR_NUMBER_INCORRECT,
  //     ERROR_CODE.CONFIRMATION_LINK_EXPIRED,
  //     ERROR_CODE.EMAIL_NOT_CONFIRMED,
  //     ERROR_CODE.EMAIL_OR_PHONE_NOT_CONFIRMED,
  //     ERROR_CODE.EMAIL_OR_PHONE_NOT_SET,
  //     ERROR_CODE.INVALID_CREDENTIALS,
  //     ERROR_CODE.INVALID_OR_EXPIRED_OTP,
  //     ERROR_CODE.INVALID_OTP,
  //     ERROR_CODE.NOT_FOUND,
  //     ERROR_CODE.PHONE_NOT_SET,
  //     ERROR_CODE.SASFIN_ACCOUNT_NOT_FOUND,
  //     ERROR_CODE.USER_ALREADY_EXIST,
  //     ERROR_CODE.USER_BLOCKED,
  //     ERROR_CODE.USER_LOCKED,
  //     ERROR_CODE.USER_NOT_ACTIVE,
  //     ERROR_CODE.USER_NOT_FOUND,
  //     ERROR_CODE.USER_NOT_PROVISIONED,
  //     ERROR_CODE.USER_TEMP_BLOCKED,
  //     ERROR_CODE.USERNAME_ALREADY_EXIST,
  //     ERROR_CODE.VALIDATION_FAILED,
  //   ];

  //   return codes.includes(code);
  // }

  private getRequest(request: HttpRequest<any>): Observable<HttpRequest<any>> {
    const token = this.authService.getTokenValue();
    if (token) {
      const headers = new HttpHeaders({
        Authorization: 'Bearer ' + token,
      });
      return of(
        request.clone({
          headers,
          withCredentials: true,
        }),
      );
    } else {
      return of(request);
    }
  }

  /**
   * Cleans validation error messages from technical details
   * @param errorMessage - original error message
   * @returns cleaned message for user
   */
  private cleanValidationErrorMessage(errorMessage: string): string {
    if (!errorMessage) {
      return 'A validation error occurred';
    }

    // Split message by semicolon to handle multiple errors
    const errors = errorMessage.split(';');
    const cleanedErrors: string[] = [];

    errors.forEach((error) => {
      const trimmedError = error.trim();

      // Skip technical JSON errors
      if (
        trimmedError.includes('JSON value could not be converted') ||
        trimmedError.includes('Path:') ||
        trimmedError.includes('LineNumber:') ||
        trimmedError.includes('BytePositionInLine:')
      ) {
        return;
      }

      // Handle standard field validation errors
      if (trimmedError.includes('field is required')) {
        const fieldMatch = trimmedError.match(/The (\w+) field is required/);
        if (fieldMatch) {
          const fieldName = this.translateFieldName(fieldMatch[1]);
          cleanedErrors.push(`The "${fieldName}" field is required`);
        } else {
          cleanedErrors.push(trimmedError);
        }
      } else if (trimmedError.length > 0) {
        // Add other errors as is, if they are not technical
        cleanedErrors.push(trimmedError);
      }
    });

    // If no errors remain after cleaning, return general message
    if (cleanedErrors.length === 0) {
      return 'Please check the form fields for correct input';
    }

    return cleanedErrors.join('. ');
  }

  /**
   * Translates field names to user-friendly names
   * @param fieldName - field name in English
   * @returns translated field name
   */
  private translateFieldName(fieldName: string): string {
    const translations: { [key: string]: string } = {
      sourceOfIncome: 'Source of Income',
      industrySectorId: 'Industry Sector',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phoneNumber: 'Phone Number',
      address: 'Address',
      dateOfBirth: 'Date of Birth',
      identityNumber: 'Identity Number',
      // Add other translations as needed
    };

    return translations[fieldName] || fieldName;
  }
}
